import {Platform, Alert, Linking} from 'react-native';

// Conditionally import PermissionsAndroid only on non-web platforms
let PermissionsAndroid = null;
if (Platform.OS === 'android') {
  try {
    PermissionsAndroid = require('react-native').PermissionsAndroid;
  } catch (e) {
    // Fallback for web or if import fails
    PermissionsAndroid = {
      PERMISSIONS: {},
      RESULTS: { GRANTED: 'granted' },
      request: () => Promise.resolve('granted'),
      check: () => Promise.resolve('granted'),
    };
  }
} else {
  // Mock PermissionsAndroid for iOS and web
  PermissionsAndroid = {
    PERMISSIONS: {},
    RESULTS: { GRANTED: 'granted' },
    request: () => Promise.resolve('granted'),
    check: () => Promise.resolve('granted'),
  };
}

export const requestStoragePermission = async () => {
  // On web, storage permissions are handled by the browser
  if (Platform.OS === 'web') {
    return true;
  }
  
  if (Platform.OS === 'android') {
    try {
      // For Android 13+ (API 33+), we need READ_MEDIA_IMAGES
      // For older versions, we use WRITE_EXTERNAL_STORAGE
      const androidVersion = Platform.Version;
      let permission;
      
      if (androidVersion >= 33) {
        permission = PermissionsAndroid.PERMISSIONS.READ_MEDIA_IMAGES;
      } else {
        permission = PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE;
      }
      
      const granted = await PermissionsAndroid.request(
        permission,
        {
          title: '📱 Storage Permission',
          message: 'Praise Devotion needs access to save and share beautiful devotion images with your friends and family.',
          buttonNeutral: 'Ask Me Later',
          buttonNegative: 'Cancel',
          buttonPositive: 'Allow',
        },
      );
      console.log('Storage permission result:', granted);
      return granted === PermissionsAndroid.RESULTS.GRANTED;
    } catch (err) {
      console.warn('Permission request error:', err);
      return false;
    }
  }
  return true;
};

export const checkStoragePermission = async () => {
  // On web, storage permissions are handled by the browser
  if (Platform.OS === 'web') {
    return true;
  }
  
  if (Platform.OS === 'android') {
    try {
      const androidVersion = Platform.Version;
      let permission;
      
      if (androidVersion >= 33) {
        permission = PermissionsAndroid.PERMISSIONS.READ_MEDIA_IMAGES;
      } else {
        permission = PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE;
      }
      
      const granted = await PermissionsAndroid.check(permission);
      return granted;
    } catch (err) {
      console.warn('Permission check error:', err);
      return false;
    }
  }
  return true;
};

export const requestPermissionWithExplanation = async () => {
  const hasPermission = await checkStoragePermission();
  
  if (!hasPermission) {
    const granted = await requestStoragePermission();
    if (!granted) {
      Alert.alert(
        '📱 Permission Required',
        'To share and save beautiful devotion images, please grant storage permission in your device settings.',
        [
          {text: 'Cancel', style: 'cancel'},
          {text: 'Open Settings', onPress: async () => {
            try {
              if (Platform.OS === 'android') {
                await Linking.openSettings();
              } else {
                await Linking.openURL('app-settings:');
              }
            } catch (error) {
              console.error('Failed to open settings:', error);
              Alert.alert('Error', 'Could not open settings. Please manually go to Settings > Apps > Praise Devotion > Permissions');
            }
          }}
        ]
      );
    }
    return granted;
  }
  
  return true;
};

export const requestCameraPermission = async () => {
  if (Platform.OS === 'android') {
    try {
      const granted = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.CAMERA,
        {
          title: '📷 Camera Permission',
          message: 'Praise Devotion needs access to your camera to take profile photos.',
          buttonNeutral: 'Ask Me Later',
          buttonNegative: 'Cancel',
          buttonPositive: 'OK',
        },
      );
      
      if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
        Alert.alert(
          'Permission Denied',
          'Camera permission is required to take photos. Please grant permission in settings.',
          [
            {text: 'Cancel', style: 'cancel'},
            {text: 'Open Settings', onPress: async () => {
              try {
                await Linking.openSettings();
              } catch (error) {
                console.error('Failed to open settings:', error);
                Alert.alert('Error', 'Could not open settings. Please manually go to Settings > Apps > Praise Devotion > Permissions');
              }
            }}
          ]
        );
        return false;
      }
      return true;
    } catch (err) {
      console.warn('Camera permission error:', err);
      return false;
    }
  }
  
  return true;
};